import { Badge, Button, Checkbox, Grid, Group, Loader, Select, Space, Stack, Text, TextInput, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { collectionGroup, DocumentData, endBefore, getCountFromServer, getDocs, limit, orderBy, query, QueryConstraint, QueryDocumentSnapshot, startAfter, Timestamp, where } from "firebase/firestore";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { IconBriefcase, IconBusinessplan, IconCheck, IconEye, IconMoneybag, IconReportMoney, IconSearch } from "@tabler/icons-react";
import { httpsCallable } from "firebase/functions";

import { MyUserDataContext } from "../../contexts";
import { useFunctions, useFirestore } from "../../helpers/firebaseContext";
import { BaseResult, ActionType, Service, ServiceActionRequest, ServiceStatusCode, ServiceType } from "../../types";
import { CurrentViewers } from "../../components/current_viewers";
import { TagBadge } from "../../components/tag_badge";
import FilterModal, { FilterItem, FilterSection } from "../../components/FilterModal";
import { FilterBadgeGroup } from "../../components/FilterBadges";
import CustomListActionsModal from "../../components/custom_actions_modal";
import { getPortfolioPendingStatusesForClaims } from "../../helpers/serviceStatusFilters";

interface ServicesFirestoreSearchProps {
    setSelectedRows?: (rows: Service[]) => void;
    selectedRows?: Service[];
    ActionsComponent?: any;
    additionalChildren?: JSX.Element;
    serviceTypes?: ServiceType[];
}

const DEFAULT_PAGE_SIZE = 50;

const SERVICE_TYPE_ICONS: Record<ServiceType, { color: string; icon: JSX.Element }> = {
    kfhbrokerage: { color: "green", icon: <IconBusinessplan size={16} /> },
    kfhtrade: { color: "orange", icon: <IconReportMoney size={16} /> },
    portfolio: { color: "grape", icon: <IconBriefcase size={16} /> },
    ipo: { color: "cyan", icon: <IconBriefcase size={16} /> },
};

const toSearchDate = (value: unknown): Date | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Timestamp) {
        return value.toDate();
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const parsedDate = new Date(value > 1_000_000_000_000 ? value : value * 1000);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "object" && value !== null) {
        if ("toDate" in value && typeof value.toDate === "function") {
            const parsedDate = value.toDate();
            return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
        }

        if ("seconds" in value && typeof value.seconds === "number") {
            const parsedDate = new Date(value.seconds * 1000);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }
    }

    return null;
};

const normalizeSearchValue = (value: unknown) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "string") {
        return value.toLowerCase();
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value).toLowerCase();
    }

    return "";
};

const getSearchableText = (service: Service) => {
    return [
        service.id,
        service.serviceType,
        service.customerObj?.name_en,
        service.customerObj?.name_ar,
        service.customerObj?.mobile,
        service.customerObj?.civilID,
        service.tags?.serviceStatus?.code,
        service.tags?.serviceStatus?.title,
        service.tags?.kycStatus?.code,
        service.tags?.riskStatus?.code,
    ].map(normalizeSearchValue).filter(Boolean).join(" ");
};

const getRestrictedStatuses = (customClaims: Record<string, any>, serviceTypes: ServiceType[]) => {
    if (serviceTypes.length === 1 && serviceTypes[0] === "portfolio") {
        return getPortfolioPendingStatusesForClaims(customClaims);
    }

    const serviceStatuses: string[] = [];

    if (customClaims.cr) {
        serviceStatuses.push(
            ServiceStatusCode.authorized_signatory_approval,
            ServiceStatusCode.waiting_for_kyc,
            ServiceStatusCode.waiting_for_execution
        );
    }

    if (customClaims.oper) {
        serviceStatuses.push(ServiceStatusCode.waiting_for_operations_approval);
    }

    return serviceStatuses;
};

const buildStaticFilterItems = (
    options: Array<{ value: string; label: string }>,
    selectedValues: string[]
): FilterItem[] => {
    return options.map((option) => ({
        value: option.value,
        label: option.label,
        count: 0,
        isRefined: selectedValues.includes(option.value),
    }));
};

const ServicesFirestoreSearch = ({
    setSelectedRows,
    selectedRows = [],
    ActionsComponent,
    additionalChildren,
    serviceTypes = ["kfhtrade", "ipo"],
}: ServicesFirestoreSearchProps) => {
    const db = useFirestore();
    const functions = useFunctions();
    const userData = useContext(MyUserDataContext);
    const customClaims = (userData.firebaseTokenResult?.claims.customClaims as Record<string, any>) || {};

    const serviceAction = httpsCallable<ServiceActionRequest, BaseResult>(functions, "serviceAction");

    const [services, setServices] = useState<Service[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState("");
    const [showAllServices, setShowAllServices] = useState(false);
    const [serviceStatusUpdateOpen, setServiceStatusUpdateOpen] = useState(false);
    const [openFilterModal, setOpenFilterModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [totalRows, setTotalRows] = useState(0);
    const [serviceStatusFilters, setServiceStatusFilters] = useState<string[]>([]);
    const [serviceTypeFilters, setServiceTypeFilters] = useState<string[]>([]);
    const [clearSelectedRowsToggle, setClearSelectedRowsToggle] = useState(false);

    const previousPageNumberRef = useRef(1);
    const firstDocInCurrentPageRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
    const lastDocInCurrentPageRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

    const restrictedStatuses = useMemo(() => getRestrictedStatuses(customClaims, serviceTypes), [customClaims, serviceTypes]);
    const serverQueryKey = useMemo(() => JSON.stringify({
        rowsPerPage,
        showAllServices,
        restrictedStatuses,
        serviceTypes,
    }), [restrictedStatuses, rowsPerPage, serviceTypes, showAllServices]);

    useEffect(() => {
        previousPageNumberRef.current = 1;
        firstDocInCurrentPageRef.current = null;
        lastDocInCurrentPageRef.current = null;
        setCurrentPage(1);
    }, [serverQueryKey]);

    useEffect(() => {
        setClearSelectedRowsToggle((current) => !current);
        setSelectedRows?.([]);
    }, [currentPage, rowsPerPage, showAllServices, setSelectedRows]);

    useEffect(() => {
        let active = true;

        const loadServices = async () => {
            setLoading(true);
            setError(null);

            try {
                const queryConstraints: QueryConstraint[] = [
                    where("serviceType", "in", serviceTypes),
                ];

                if (!showAllServices && restrictedStatuses.length > 0) {
                    queryConstraints.push(where("tags.serviceStatus.code", "in", restrictedStatuses));
                }

                const baseQuery = query(
                    collectionGroup(db, "services"),
                    ...queryConstraints,
                    orderBy("created", "desc")
                );

                let pagedQuery;

                if (currentPage <= 1 || previousPageNumberRef.current === currentPage) {
                    pagedQuery = query(baseQuery, limit(rowsPerPage));
                } else if (currentPage > previousPageNumberRef.current && lastDocInCurrentPageRef.current) {
                    pagedQuery = query(baseQuery, startAfter(lastDocInCurrentPageRef.current), limit(rowsPerPage));
                } else if (currentPage < previousPageNumberRef.current && firstDocInCurrentPageRef.current) {
                    pagedQuery = query(baseQuery, endBefore(firstDocInCurrentPageRef.current), limit(rowsPerPage));
                } else {
                    pagedQuery = query(baseQuery, limit(rowsPerPage));
                }

                const [countSnapshot, servicesSnapshot] = await Promise.all([
                    getCountFromServer(baseQuery),
                    getDocs(pagedQuery),
                ]);

                if (!active) {
                    return;
                }

                firstDocInCurrentPageRef.current = servicesSnapshot.docs[0] ?? null;
                lastDocInCurrentPageRef.current = servicesSnapshot.docs[servicesSnapshot.docs.length - 1] ?? null;
                previousPageNumberRef.current = currentPage;

                setServices(servicesSnapshot.docs.map((docSnapshot) => ({
                    ...(docSnapshot.data() as Service),
                    id: docSnapshot.id,
                })));
                setTotalRows(countSnapshot.data().count);
            } catch (loadError: any) {
                if (!active) {
                    return;
                }

                setServices([]);
                setTotalRows(0);
                setError(loadError?.message || "Failed to load services from Firestore.");
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadServices();

        return () => {
            active = false;
        };
    }, [currentPage, db, restrictedStatuses, rowsPerPage, serviceTypes, showAllServices]);

    const normalizedSearchText = searchText.trim().toLowerCase();

    const visibleServices = useMemo(() => {
        return services.filter((service) => {
            if (normalizedSearchText && !getSearchableText(service).includes(normalizedSearchText)) {
                return false;
            }

            if (serviceStatusFilters.length > 0 && !serviceStatusFilters.includes(service.tags?.serviceStatus?.code || "")) {
                return false;
            }

            if (serviceTypeFilters.length > 0 && !serviceTypeFilters.includes(service.serviceType || "")) {
                return false;
            }

            return true;
        });
    }, [normalizedSearchText, serviceStatusFilters, serviceTypeFilters, services]);

    const totalPages = totalRows > 0 ? Math.ceil(totalRows / rowsPerPage) : 1;

    const toggleFilter = (values: string[], setValues: (values: string[]) => void, value: string) => {
        if (values.includes(value)) {
            setValues(values.filter((existingValue) => existingValue !== value));
            return;
        }

        setValues([...values, value]);
    };

    const serviceStatusItems = useMemo(
        () => buildStaticFilterItems(
            Object.values(ServiceStatusCode).map((value) => ({ value, label: value })),
            serviceStatusFilters
        ),
        [serviceStatusFilters]
    );

    const serviceTypeItems = useMemo(
        () => buildStaticFilterItems(
            serviceTypes.map((value) => ({ value, label: value })),
            serviceTypeFilters
        ),
        [serviceTypeFilters, serviceTypes]
    );

    const columns: TableColumn<Service>[] = [
        {
            id: "type",
            cell: (row) => {
                const icons = [];
                const serviceTypeMeta = SERVICE_TYPE_ICONS[row.serviceType];

                if (serviceTypeMeta) {
                    icons.push(
                        <ThemeIcon key={row.serviceType} variant="white" color={serviceTypeMeta.color}>
                            {serviceTypeMeta.icon}
                        </ThemeIcon>
                    );
                }

                if (row.tags?.paid) {
                    icons.push(
                        <ThemeIcon key="paid" variant="white" color="yellow">
                            <IconMoneybag size={16} />
                        </ThemeIcon>
                    );
                }

                if (row.serviceType === "kfhtrade" && !!row.customerObj?.kfhtrade_id?.length) {
                    icons.push(
                        <ThemeIcon key="kfhtrade" variant="white" color="blue">
                            <IconCheck size={16} />
                        </ThemeIcon>
                    );
                }

                return <>{icons}</>;
            },
            width: "90px",
        },
        {
            name: <Tooltip label="Current Viewers" transitionProps={{ transition: "scale", duration: 500 }}><IconEye size={16} /></Tooltip>,
            cell(row) {
                return <CurrentViewers type="services" id={row.id!} />;
            },
            width: "90px",
        },
        {
            name: "Name",
            selector: (row) => row.customerObj?.name_en ?? "",
        },
        {
            name: "Mobile",
            selector: (row) => row.customerObj?.mobile ?? "",
            grow: 1,
        },
        {
            name: "Civil ID",
            selector: (row) => row.customerObj?.civilID ?? "",
            grow: 1,
        },
        {
            name: "Date",
            cell: (row) => {
                const created = toSearchDate(row.created);
                return created ? created.toLocaleString("en-GB", { hour12: true }) : String(row.created ?? "-");
            },
        },
        {
            name: "Tags",
            cell: (row: Service) => {
                if (!row.tags) {
                    return null;
                }

                return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        <TagBadge m={3} tag={row.tags.serviceStatus} key="serviceStatus" />
                        {row.tags.riskStatus && <TagBadge m={3} tag={row.tags.riskStatus} key="riskStatus" />}
                        {row.tags.kycStatus && <TagBadge m={3} tag={row.tags.kycStatus} key="kycStatus" />}
                    </div>
                );
            },
        },
        {
            name: "",
            selector: (row: any) => <>{ActionsComponent && <ActionsComponent serviceObj={row} />}</> as any,
            grow: 0,
        },
    ];

    const customerModals = (
        <CustomListActionsModal<Service>
            rows={selectedRows}
            handleClose={() => {
                setSelectedRows?.([]);
                setClearSelectedRowsToggle((prev) => !prev);
            }}
            opened={serviceStatusUpdateOpen}
            setOpened={setServiceStatusUpdateOpen}
            availableActions={[
                {
                    value: "authorized_signatory_approve",
                    label: "Authorized Signatory Approve"
                },
                {
                    value: "execution",
                    label: "Execute"
                },
                {
                    value: "activation",
                    label: "Activate"
                },
            ]}
            handleSubmit={async (serviceData: Service, action: string, token?: string) => {
                if (!serviceData.id || !serviceData.customerObj?.id || !token) {
                    throw new Error("Missing required data for service action.");
                }
                await serviceAction({
                    action: action as ActionType,
                    serviceID: serviceData.id,
                    customerID: serviceData.customerObj.id,
                    token,
                });
            }}
            renderErrors={(failedItem: any) => <>
                {failedItem.name_en}
                <Space h="xl" />
            </>}
            getItemsData={(action: string, rows: Service[]) => {
                let data: Service[] = [];

                if (action === "authorized_signatory_approve") {
                    data = rows.filter((service) => service?.tags?.serviceStatus.code === "authorized_signatory_approval");
                }

                if (action === "add_subaccount_id") {
                    data = rows.filter((service) => service?.tags?.serviceStatus.code === "waiting_for_sub_account_id");
                }

                if (action === "execution") {
                    data = rows.filter((service) => service?.tags?.serviceStatus.code === "waiting_for_execution");
                }

                if (action === "activation") {
                    data = rows.filter((service) => service?.tags?.serviceStatus.code === "waiting_for_activation");
                }

                return data;
            }}
        />
    );

    if (loading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        );
    }

    if (error) {
        return (
            <Stack gap="sm">
                <Badge color="orange" variant="light">
                    Firestore fallback mode
                </Badge>
                <Text c="red">{error}</Text>
            </Stack>
        );
    }

    return (
        <>
            {customerModals}

            <Grid justify="center" align="center">
                <Grid.Col span={12}>
                    <Group justify="space-between" align="flex-start">
                        <Group gap="xs" align="center">
                            <Badge color="orange" variant="light">
                                Firestore fallback mode
                            </Badge>
                            <Text size="sm" c="dimmed">
                                Services are paged directly from Firestore. Text search and filter badges apply to the current page.
                            </Text>
                        </Group>
                    </Group>

                    <Title size="md" my="sm">
                        Search
                    </Title>

                    <Group>
                        <TextInput
                            leftSection={<IconSearch size={16} />}
                            style={{ flex: 1 }}
                            value={searchText}
                            onChange={(event) => setSearchText(event.currentTarget.value)}
                            placeholder="Filter the current page by customer, service type, status, or ID..."
                        />
                        <Button onClick={() => setOpenFilterModal(true)}>
                            Filter
                        </Button>
                    </Group>

                    <FilterBadgeGroup
                        items={serviceStatusItems}
                        color="blue"
                        refine={(value) => toggleFilter(serviceStatusFilters, setServiceStatusFilters, value)}
                    />
                    <FilterBadgeGroup
                        items={serviceTypeItems}
                        color="yellow"
                        refine={(value) => toggleFilter(serviceTypeFilters, setServiceTypeFilters, value)}
                    />
                </Grid.Col>

                <Grid.Col span={12} mt={0}>
                    <Group justify="space-between">
                        <Checkbox
                            label="Show all services"
                            checked={showAllServices}
                            onChange={(event) => setShowAllServices(event.currentTarget.checked)}
                        />

                        <Button
                            color="lime"
                            onClick={() => setServiceStatusUpdateOpen(true)}
                            disabled={selectedRows.length === 0}
                        >
                            {`Change Service status for ${selectedRows.length} service(s)`}
                        </Button>

                        {additionalChildren}
                    </Group>

                    <Space h="sm" />

                    <DataTable
                        columns={columns}
                        selectableRows
                        clearSelectedRows={clearSelectedRowsToggle}
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows?.(selectedRows as Service[])}
                        data={visibleServices}
                        noDataComponent={(
                            <Text size="sm" c="dimmed" py="lg">
                                No services on this page match the current page filters.
                            </Text>
                        )}
                    />
                </Grid.Col>

                <FilterModal
                    opened={openFilterModal}
                    onClose={() => setOpenFilterModal(false)}
                    sections={[
                        {
                            id: "serviceStatus",
                            title: "Filter by Service Status",
                            items: serviceStatusItems,
                            refine: (value) => toggleFilter(serviceStatusFilters, setServiceStatusFilters, value),
                        },
                        {
                            id: "serviceType",
                            title: "Filter by Service Type",
                            items: serviceTypeItems,
                            refine: (value) => toggleFilter(serviceTypeFilters, setServiceTypeFilters, value),
                        },
                    ] as FilterSection[]}
                />
            </Grid>

            <Group justify="space-between" mt="md" align="flex-end">
                <Stack gap={2}>
                    <Text size="sm">
                        Page {currentPage} of {Math.max(totalPages, 1)}
                    </Text>
                    <Text size="xs" c="dimmed">
                        Loaded {services.length} rows from Firestore for this page. {totalRows.toLocaleString()} total rows match the server-side query.
                    </Text>
                </Stack>

                <Group align="flex-end">
                    <Select
                        label="Rows per page"
                        value={String(rowsPerPage)}
                        data={[
                            { value: "50", label: "50" },
                            { value: "100", label: "100" },
                            { value: "250", label: "250" },
                        ]}
                        onChange={(value) => setRowsPerPage(Number(value || DEFAULT_PAGE_SIZE))}
                        allowDeselect={false}
                        w={120}
                    />

                    <Button
                        variant="default"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    >
                        Newer
                    </Button>
                    <Button
                        variant="default"
                        disabled={currentPage >= totalPages || totalRows === 0}
                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    >
                        Older
                    </Button>
                </Group>
            </Group>
        </>
    );
};

export default ServicesFirestoreSearch;
